// A tudástár katalógusa. 2026-07-31-ig ez volt a `/` — a kezdőlap azóta külön belépő, mert
// „egyből a katalógusba érkezni" azt feltételezi, hogy mindenki böngészni jött. Itt tehát az van,
// amiért valaki KIFEJEZETTEN a katalógust nyitja meg: a teljes terület-lista, szűrővel.
import { Bars3Icon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { PERMISSIONS } from '@morgens-hub/shared';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useStructure } from '@/features/editor/api';
import { useKnowledgeTree } from '@/features/knowledge/api';
import { InlineRename } from '@/features/knowledge/components/inline-rename';
import { KnowledgeLayout } from '@/features/knowledge/components/knowledge-layout';
import { SpaceIndex } from '@/features/knowledge/components/space-index';
import { SpaceTree } from '@/features/knowledge/components/space-tree';
import { TreeFilter } from '@/features/knowledge/components/tree-filter';
import { useStructureMutations } from '@/features/knowledge/hooks/use-structure-mutations';
import { requireAuth } from '@/lib/auth';
import { useHasPermission } from '@/lib/use-permission';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/tudastar')({
  // A nyitott ágak az URL-ben élnek: így a nézet megosztható és visszatérésre megmarad.
  validateSearch: (search: Record<string, unknown>): { nyitva?: string; szerkesztes?: '1' } => ({
    ...(typeof search.nyitva === 'string' && search.nyitva ? { nyitva: search.nyitva } : {}),
    // A router a `?szerkesztes=1`-et SZÁMKÉNT parse-olja, ezért string-összehasonlítás kell.
    ...(String(search.szerkesztes) === '1' ? { szerkesztes: '1' as const } : {}),
  }),
  beforeLoad: ({ location }) => requireAuth(location.href),
  component: KnowledgePage,
});

/** Ékezet-tűrő összehasonlítás a szűréshez („uzemeltetes" is találjon az „Üzemeltetés"-re). */
const fold = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

function KnowledgePage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [treeOpen, setTreeOpen] = useState(false);
  const [treeFilter, setTreeFilter] = useState('');
  // A találatokból adódó nyitás MÚLÉKONY állapot: nem kerül az URL-be, mert az a gépelés
  // mellékterméke, nem megosztható nézet.
  const [autoOpen, setAutoOpen] = useState<readonly string[]>([]);
  // Nyitva van-e az „Új szekció" űrlap. Nem kerül az URL-be: félbehagyott gépelést nem osztunk meg.
  const [creatingSpace, setCreatingSpace] = useState(false);
  // A szekció-létrehozás UTÁNI továbbvezetés (Task 2): melyik friss szekcióhoz ajánljuk fel a
  // könyv-létrehozást, és nyitva van-e már az inline könyv-űrlap. Nem kerül az URL-be — múlékony,
  // egyszeri javaslat, nem megosztható nézet.
  const [newSpacePrompt, setNewSpacePrompt] = useState<{ id: string; name: string } | null>(null);
  const [creatingCategoryForNewSpace, setCreatingCategoryForNewSpace] = useState(false);
  const tree = useKnowledgeTree();
  const { nyitva, szerkesztes } = Route.useSearch();

  const canManageSpaces = useHasPermission(PERMISSIONS.spaceManage);
  const canManageCategories = useHasPermission(PERMISSIONS.categoryManage);
  const canEditStructure = canManageSpaces || canManageCategories;
  const editMode = szerkesztes === '1' && canEditStructure;

  // Szerkesztés-módban MÁS az adatforrás: az `/editor/structure` adja a `version`-t, az archivált
  // elemeket és a láthatóságot — az olvasó fa-végpont ezeket szándékosan nem adja (F1). A hívás csak
  // szerkesztés-módban indul.
  const structure = useStructure({ enabled: editMode });
  const structureBody = structure.data?.status === 200 ? structure.data.body : null;

  // `onDone` fut MINDEN sikeres szerkezet-műveletre (átnevezés, törlés, stb. is) — a
  // `newSpacePrompt` törlése itt tehát azt jelenti: bármelyik további sikeres szerkesztés eldönti a
  // javaslatot (akár a felajánlott könyvet hozta létre a felhasználó, akár mással foglalkozott
  // tovább). Ez a szándékolt viselkedés: egyszeri, múlékony nudge, nem állandó UI-elem.
  const mutations = useStructureMutations(() => {
    setCreatingSpace(false);
    setCreatingCategoryForNewSpace(false);
    setNewSpacePrompt(null);
  });

  const editing = {
    canManageSpaces,
    canManageCategories,
    canManageArticles: useHasPermission(PERMISSIONS.articleManage),
    canDeleteSpaces: useHasPermission(PERMISSIONS.spaceDelete),
    canDeleteCategories: useHasPermission(PERMISSIONS.categoryDelete),
    canPublishPublic: useHasPermission(PERMISSIONS.articlePublishPublic),
  };

  // A nyitott csoport-címek és szekció-slugok listája az URL-ből.
  const open = useMemo(() => (nyitva ? nyitva.split(',').filter(Boolean) : []), [nyitva]);
  const onMatches = useCallback((slugs: readonly string[]) => {
    setAutoOpen(slugs);
  }, []);

  const onToggle = (key: string): void => {
    const next = open.includes(key) ? open.filter((k) => k !== key) : [...open, key];
    void navigate({
      to: '/tudastar',
      // A `szerkesztes` ITT is megy tovább: a `navigate` a teljes keresőparaméter-készletet
      // felülírja, tehát ha kimaradna, az első ág-nyitás kikapcsolná a szerkesztés-módot.
      search: {
        ...(next.length > 0 ? { nyitva: next.join(',') } : {}),
        ...(szerkesztes === '1' ? { szerkesztes: '1' as const } : {}),
      },
      replace: true,
    });
  };

  const body = tree.data?.status === 200 ? tree.data.body : null;

  // A mező kettős munkát végez: gépelés közben SZŰKÍTI a katalógust, Enterre TELJES SZÖVEGŰ
  // keresésre visz. Így nem kell előre eldönteni, hogy „böngészek" vagy „keresek".
  //
  // KORREKCIÓ (2026-08-14, éles lelet): a katalógus korábban NÉMÁN kihagyta a 0 cikkes
  // szekciókat — de a fa (`treeSpaces` lent) MUTATTA őket, tehát ugyanaz az oldal két
  // ellentmondó választ adott ugyanarra a kérdésre. Ez pont azt a szekciót tüntette el, amit a
  // felhasználó ÉPP AKKOR hozott létre — „mentve, de nincs ott" élményt okozva. Élesben mérve
  // (2026-08-14): 44 szekcióból 7 (≈16%) 0 cikkes — nem elhanyagolható arány, tehát a NÉMA
  // elrejtés helyett a `SpaceIndex` most MEGKÜLÖNBÖZTETVE mutatja őket (halvány „Üres" jelölés),
  // a fa-nézettel megegyezően.
  const spaces = useMemo(() => {
    if (!body) return [];
    const needle = fold(query.trim());
    if (!needle) return body.spaces;
    return body.spaces.filter(
      (s) =>
        fold(s.name).includes(needle) ||
        fold(s.description).includes(needle) ||
        s.categories.some((c) => fold(c.name).includes(needle)),
    );
  }, [body, query]);

  // A FA a teljes tudástárat mutatja (üres szekciókkal együtt), de a szűrő-mező szűkíti — névre a
  // szekciókon és a könyveken, cikknévre pedig a kereső találatain át (lásd TreeFilter).
  const treeSpaces = useMemo(() => {
    if (!body) return [];
    const needle = fold(treeFilter.trim());
    if (!needle) return body.spaces;
    return body.spaces.filter(
      (s) =>
        fold(s.name).includes(needle) ||
        s.categories.some((c) => fold(c.name).includes(needle)) ||
        autoOpen.includes(s.slug),
    );
  }, [body, treeFilter, autoOpen]);

  // A statisztika a KATALÓGUSBAN LÁTHATÓ szekció-számot mutatja — most már az üreseket is
  // beleértve, mert a katalógus is mutatja őket (lásd a `spaces` fenti kommentjét).
  const spaceCount = body?.spaces.length ?? 0;
  const totalArticles = body?.spaces.reduce((n, s) => n + s.articleCount, 0) ?? 0;

  return (
    <KnowledgeLayout showTree={false}>
      <div className="lg:flex lg:gap-8">
        {/* Bal panel: a TELJES tudástár fája. Mobilon nyitóval, EGY renderelt fával — a kettős
            renderelés (mobil + asztali példány) az F0-mérés szerint duplázta a DOM-linkeket. */}
        <aside className="mb-6 lg:mb-0 lg:w-64 lg:shrink-0">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mb-3 w-full justify-start gap-2 lg:hidden"
            aria-expanded={treeOpen}
            onClick={() => {
              setTreeOpen((isOpen) => !isOpen);
            }}
          >
            <Bars3Icon className="h-4 w-4" aria-hidden />
            {t('knowledge.treeAll')}
          </Button>

          <div
            className={cn(
              'lg:sticky lg:top-20 lg:block lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto lg:pr-2',
              treeOpen ? 'block' : 'hidden',
            )}
          >
            <TreeFilter value={treeFilter} onChange={setTreeFilter} onMatches={onMatches} />
            {(editMode ? structureBody : body) && (
              <SpaceTree
                spaces={editMode && structureBody ? structureBody.spaces : treeSpaces}
                open={[...open, ...autoOpen]}
                onToggle={onToggle}
                {...(editMode ? { editing } : {})}
              />
            )}
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <header className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-read-2xl font-semibold tracking-tight">{t('knowledge.title')}</h1>
              <p className="text-muted-foreground text-read-sm mt-2 max-w-prose">
                {t('knowledge.subtitle')}
              </p>
            </div>

            <div className="flex items-center gap-2">
              {/* Az „Új szekció" ITT van, nem a fában: az új szekciónak nincs szülő-kontextusa,
                  amihez hozzá lehetne rendelni. (A könyvnek van — az ezért a szekció
                  művelet-sorában él.) */}
              {editMode && canManageSpaces && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setCreatingSpace(true);
                  }}
                >
                  {t('structure.newSpace')}
                </Button>
              )}

              {/* A kapcsoló CSAK szerkezet-joggal jelenik meg: az olvasók 90%-a nem szerkeszt, és
                  egy tiltott gomb csak zaj lenne. Az állapot az URL-ben él, tehát megosztható. */}
              {canEditStructure && (
                <Button
                  type="button"
                  size="sm"
                  variant={editMode ? 'secondary' : 'outline'}
                  onClick={() => {
                    setCreatingSpace(false);
                    void navigate({
                      to: '/tudastar',
                      search: {
                        ...(nyitva ? { nyitva } : {}),
                        ...(editMode ? {} : { szerkesztes: '1' as const }),
                      },
                      replace: true,
                    });
                  }}
                >
                  {editMode ? t('structure.editModeOn') : t('structure.editMode')}
                </Button>
              )}
            </div>
          </header>

          {editMode && canManageSpaces && creatingSpace && (
            <div className="mt-4 max-w-xl">
              <InlineRename
                initial=""
                pending={mutations.pending}
                onCancel={() => {
                  setCreatingSpace(false);
                }}
                onSubmit={(name) => {
                  mutations.createSpace(name, (space) => {
                    setNewSpacePrompt(space);
                  });
                }}
              />
            </div>
          )}

          {/* Task 2 — a hierarchia (szekció → könyv → cikk) ma sehol nem derül ki: az „Új cikk"
              oldal csak akkor mutat súgót, ha a felhasználó MÁR odáig eljutott. Szekció
              létrehozása UTÁN itt, azonnal ajánljuk fel a következő lépést — egy egyértelmű
              pont, nem varázsló. */}
          {editMode && canManageCategories && newSpacePrompt && (
            <div className="border-border bg-muted/40 mt-4 max-w-xl rounded-lg border p-4">
              {creatingCategoryForNewSpace ? (
                <InlineRename
                  initial=""
                  pending={mutations.pending}
                  onCancel={() => {
                    setCreatingCategoryForNewSpace(false);
                  }}
                  onSubmit={(name) => {
                    mutations.createCategory(newSpacePrompt.id, name);
                  }}
                />
              ) : (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">
                      {t('knowledge.newCategoryPromptTitle', { name: newSpacePrompt.name })}
                    </p>
                    <p className="text-muted-foreground text-sm">
                      {t('knowledge.newCategoryPromptBody')}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setNewSpacePrompt(null);
                      }}
                    >
                      {t('knowledge.newCategoryPromptDismiss')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        setCreatingCategoryForNewSpace(true);
                      }}
                    >
                      {t('knowledge.newCategoryPromptAction')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          <form
            role="search"
            className="relative mt-7 max-w-xl"
            onSubmit={(event) => {
              event.preventDefault();
              const q = query.trim();
              if (q) void navigate({ to: '/kereses', search: { q } });
            }}
          >
            <MagnifyingGlassIcon
              className="text-subtle pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2"
              aria-hidden
            />
            <Input
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
              }}
              placeholder={t('knowledge.filterPlaceholder')}
              aria-label={t('knowledge.filterPlaceholder')}
              className="h-12 pl-12 text-base"
            />
          </form>
          <p className="text-subtle mt-2 text-sm">
            {query.trim()
              ? t('knowledge.searchHint', { query: query.trim() })
              : t('knowledge.stats', { spaces: spaceCount, articles: totalArticles })}
          </p>

          {tree.isLoading && (
            <div className="mt-10 space-y-3">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          )}

          {body && spaces.length > 0 && (
            <section className="mt-8">
              <SpaceIndex spaces={spaces} />
            </section>
          )}

          {body && body.spaces.length > 0 && spaces.length === 0 && (
            <p className="text-muted-foreground mt-10 text-sm">
              {t('knowledge.filterEmpty', { filter: query })}
            </p>
          )}

          {body?.spaces.length === 0 && (
            <p className="text-muted-foreground mt-10 text-sm">{t('knowledge.emptyHome')}</p>
          )}

          {!tree.isLoading && !body && (
            <p className="text-destructive mt-10 text-sm">{t('knowledge.loadError')}</p>
          )}
        </div>
      </div>
    </KnowledgeLayout>
  );
}
